import { apiSlice } from "@/shared/api"

import { DEFAULT_PERIOD, DEFAULT_SCOPE } from "../model/constants"
import type {
  LeaderboardEntry,
  LeaderboardEntryResponse,
  LeaderboardModel,
  LeaderboardPeriod,
  LeaderboardResponse,
  LeaderboardScope
} from "../model/types"

import type { GetGroupRatingRequest, GetGroupRatingResponse, GetLeaderboardPageParam } from "./types"

const mapLeaderboardEntry = (entry: LeaderboardEntryResponse): LeaderboardEntry => ({
  position: entry.position,
  userId: entry.user_id,
  displayName: entry.display_name,
  avatarUrl: entry.avatar_url,
  level: entry.level,
  levelName: entry.level_name,
  levelEmoji: entry.level_emoji,
  xp: entry.xp,
  xpChange: entry.xp_change
})

const mapLeaderboardResponse = (data: LeaderboardResponse): LeaderboardModel => ({
  entries: data.entries.map(mapLeaderboardEntry),
  myPosition: data.my_position ? mapLeaderboardEntry(data.my_position) : null
})

export const getGroupRatingApi = apiSlice
  .enhanceEndpoints({ addTagTypes: ["GroupRating", "RankUpdate", "PermissionUpdate", "Leaderboard"] })
  .injectEndpoints({
    endpoints: builder => ({
      getGroupRating: builder.query<GetGroupRatingResponse, GetGroupRatingRequest>({
        query: chatId => `/api/v1/group/rating?chat_id=${chatId}`,
        providesTags: ["GroupRating", "RankUpdate", "PermissionUpdate"]
      }),

      getLeaderboard: builder.query<
        LeaderboardModel,
        { chatId: number; period?: LeaderboardPeriod; scope?: LeaderboardScope; limit?: number }
      >({
        query: ({ chatId, period = DEFAULT_PERIOD, scope = DEFAULT_SCOPE, limit = 10 }) => ({
          url: `/api/v1/leaderboard/${chatId}`,
          params: { period, scope, limit }
        }),
        transformResponse: (response: LeaderboardResponse) => mapLeaderboardResponse(response),
        providesTags: ["Leaderboard"]
      }),

      getLeaderboardPage: builder.infiniteQuery<
        LeaderboardModel,
        { chatId: number; period?: LeaderboardPeriod; scope?: LeaderboardScope },
        GetLeaderboardPageParam
      >({
        infiniteQueryOptions: {
          initialPageParam: { offset: 0, limit: 50 },
          getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            if (lastPage.entries.length < lastPageParam.limit) {
              return undefined
            }

            return {
              offset: lastPageParam.offset + lastPage.entries.length,
              limit: lastPageParam.limit
            }
          }
        },
        query: ({ queryArg: { chatId, period = DEFAULT_PERIOD, scope = DEFAULT_SCOPE }, pageParam }) => ({
          url: `/api/v1/leaderboard/${chatId}`,
          params: {
            period,
            scope,
            limit: pageParam.limit,
            offset: pageParam.offset
          }
        }),
        transformResponse: (response: LeaderboardResponse) => mapLeaderboardResponse(response),
        providesTags: (_result, _error, { chatId, period = DEFAULT_PERIOD, scope = DEFAULT_SCOPE }) => [
          { type: "Leaderboard" as const, id: `CHAT_${chatId}_${period}_${scope}` }
        ]
      })
    })
  })

export const { useGetGroupRatingQuery, useGetLeaderboardQuery, useGetLeaderboardPageInfiniteQuery } = getGroupRatingApi
